// Render plugin for `composite_table_rendered` SSE events.
//
// A composite is a table the agent assembled from numbers it extracted or
// computed, with per-cell provenance. Clicking a cell opens ProvenanceModal,
// which is why this plugin owns the modal state rather than CompositeTable.
import React, { useMemo, useState } from 'react'
import axios from 'axios'
import type { RenderPlugin, PluginAgentSSEEvent } from '../agent'
import { CARD_SX, pluginEventId, useAgentCardHost } from '../agent'
import { Box, IconButton, Tooltip } from '@mui/material'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import CompositeTable from '../components/composite-table/CompositeTable'
import ProvenanceModal from '../components/composite-table/ProvenanceModal'
import type { CellProvenance, ProvenanceSidecar, RunReceipt } from '../components/composite-table/provenance.generated'
import type { PlatinumData } from '../types/carbon/models/PlatinumData'
import type { DatasetLoader, TextLoader } from '../components/composite-table/ProvenanceModal'
import { API_BASE_URL } from '../constants/system'

interface CompositeEntry {
  id: string
  platinumData: unknown
  provenance?: ProvenanceSidecar
  title: string
}

type CompositePluginState = Map<string, CompositeEntry>

/**
 * Fetch a source dataset for the provenance modal.
 *
 * There is no route that reads a session file by path — the API exposes
 * `GET /agent/session/:id/artifacts` and `GET /agent/artifacts/:id/download`
 * (goapi/pkg/server/agent.go:57-58). So this lists the session's artifacts,
 * finds the one whose file_path is `data/<name>.json`, and downloads it by id.
 * The listing is fetched once and reused, because a derived cell can reference
 * several datasets and each would otherwise re-list.
 *
 * `file_path`, snake_case: the listing serialises goapi's types.AgentArtifact,
 * not agentkit's camelCase Artifact. Reading `filePath` matched nothing, so
 * every source lookup in the chat failed as "not among this session's files".
 *
 * The modal never fetches on its own — it is handed this — which is what lets
 * the same modal work inside a published page, where the loader instead reads a
 * sibling file off disk.
 */
interface ListedArtifact {
  id: string
  file_path: string
}

/**
 * ONE way a session file is found, for datasets and scripts alike.
 *
 * Both the table behind a number and the code that produced it are session
 * artifacts, matched the same way. If the two ever disagreed about how a
 * `file_path` maps to an artifact, the chat and the published page would tell a
 * reader two different stories about the same number — so there is one matcher,
 * and the loaders below differ only in what they ask it for.
 */
function makeArtifactReader(sessionId: string, apiBase: string) {
  let listing: Promise<ListedArtifact[]> | null = null

  // axios, not fetch: the app's auth lives in axios.defaults.headers.common,
  // so a raw fetch here would be unauthenticated.
  const artifacts = () => {
    if (!listing) {
      listing = axios
        .get<{ artifacts: ListedArtifact[] }>(
          `${apiBase}/agent/session/${encodeURIComponent(sessionId)}/artifacts`,
        )
        .then((res) => res.data?.artifacts ?? [])
        .catch((e) => {
          listing = null   // let a later click retry rather than caching the failure
          throw new Error(`could not list the session's files (${e?.message ?? e})`)
        })
    }
    return listing
  }

  const idFor = async (filePath: string): Promise<string> => {
    const found = (await artifacts()).find((a) => a.file_path === filePath)
    if (!found) {
      throw new Error(`${filePath} is not among this session's files — it may have been renamed or never saved`)
    }
    return found.id
  }

  const url = (id: string) => `${apiBase}/agent/artifacts/${encodeURIComponent(id)}/download`

  return {
    async json<T>(filePath: string): Promise<T> {
      const res = await axios.get<T>(url(await idFor(filePath)))
      return res.data
    },
    async text(filePath: string): Promise<string> {
      // responseType + an identity transform, so a source file is never parsed.
      // axios's default transform tries JSON.parse first, which would turn a
      // script that happens to be valid JSON into an object and then into
      // "[object Object]" on screen.
      const res = await axios.get<string>(url(await idFor(filePath)), {
        responseType: 'text',
        transformResponse: [(d) => d],
      })
      return res.data
    },
  }
}

export function makeChatDatasetLoader(sessionId: string, apiBase: string): DatasetLoader {
  const reader = makeArtifactReader(sessionId, apiBase)
  return (name: string) => reader.json<PlatinumData>(`data/${name}.json`)
}

/**
 * Read a shipped script for the modal's code viewer.
 *
 * The path is APP-RELATIVE (`engine.py`), because a published webapp IS the
 * chat folder's `data/` — exactly as a dataset arrives as a bare name. This is the
 * chat-side half of the same seam webviz's `provenance.js` fills with a sibling
 * fetch; ProvenanceModal's `appRelativeScriptPath` has already established that
 * the file ships before either is called.
 */
export function makeChatScriptLoader(sessionId: string, apiBase: string): TextLoader {
  const reader = makeArtifactReader(sessionId, apiBase)
  return (appRelativePath: string) => reader.text(`data/${appRelativePath}`)
}

const CompositeTables: React.FC<{ entries: CompositeEntry[]; sessionId: string }> = ({ entries, sessionId }) => {
  // The cell AND the receipt of the composite it came from. One modal serves
  // every table here, and the receipt is per composite — carrying the cell
  // alone would show one table's replay tick above another table's number.
  const [open, setOpen] = useState<{ provenance: CellProvenance; run?: RunReceipt } | null>(null)
  const loadDataset = useMemo(() => makeChatDatasetLoader(sessionId, API_BASE_URL), [sessionId])
  const loadScript = useMemo(() => makeChatScriptLoader(sessionId, API_BASE_URL), [sessionId])
  const { openNotebook } = useAgentCardHost()

  return (
    <>
      {entries.map((entry) => (
        // The same card as every other agent-rendered widget; an unframed
        // MUI Table reads as an admin screen dropped into the conversation.
        <Box key={entry.id} data-testid="composite-table-card" sx={{ ...CARD_SX, my: 1, p: 1.5, position: 'relative' }}>
          {openNotebook && (
            <Tooltip title="Open in notebook">
              <IconButton
                size="small"
                aria-label="Open in notebook"
                data-testid="open-in-notebook"
                onClick={() => openNotebook(entry.id)}
                sx={{ position: 'absolute', top: 6, right: 6, color: 'info.main', p: 0.25 }}
              >
                <NotesOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <CompositeTable
            data={entry.platinumData as never}
            provenance={entry.provenance}
            title={entry.title || undefined}
            onCellClick={(_row, _col, provenance) => setOpen({ provenance, run: entry.provenance?.run })}
          />
        </Box>
      ))}
      <ProvenanceModal
        open={open !== null}
        onClose={() => setOpen(null)}
        provenance={open?.provenance ?? null}
        run={open?.run}
        loadDataset={loadDataset}
        loadScript={loadScript}
      />
    </>
  )
}

export const compositeTablePlugin: RenderPlugin<CompositePluginState> = {
  eventTypes: ['composite_table_rendered'],

  init(): CompositePluginState {
    return new Map()
  },

  reduce(state: CompositePluginState, event: PluginAgentSSEEvent): CompositePluginState {
    const d = event.data
    // Key on the event `id`, which the marker dispatcher stamps unconditionally.
    // A bare toolCallId fallback would let several composites from one tool call
    // overwrite each other and only the last would show — the same trap
    // carbonTablePlugin documents. The fold over pluginEvents is deterministic,
    // so suffixing with the fold position replays to the same keys.
    const id = pluginEventId(d, state.size)
    if (!id) return state
    const next = new Map(state)
    next.set(id, {
      id,
      platinumData: d.platinumData,
      provenance: d.provenance as ProvenanceSidecar | undefined,
      title: (d.title as string) || '',
    })
    return next
  },

  render({ state, sessionId }: { state: CompositePluginState; toolCallId: string; sessionId: string }) {
    if (state.size === 0) return null
    return <CompositeTables entries={Array.from(state.values())} sessionId={sessionId} />
  },
}

export default compositeTablePlugin
